import { useAppSelector } from '@brands/hooks/useReduxHook';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { UserRoleName } from '@brands/services/identity/types/UserProfile';
import { selectAuth } from '@brands/store/selectors/auth';
import { canGoBackInApp } from '@brands/Utils/canGoBackInApp';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Close handler for the Oral Health (Coaching) Report pages. It goes back when
 * the report was opened from another page of the app. Otherwise (an email link,
 * a new tab) it lands on the Care History the report belongs to: a guardian
 * closing a dependent's report gets that dependent's history, picked through
 * the same `?patientId=` that OpenCaseListModal uses for a dependent's
 * appointments, instead of their own.
 */
export default function useCloseReport(reportPatientId?: number): () => void {
  const navigate = useNavigate();
  const roleNavigate = useRoleNavigate();
  const { userInfo } = useAppSelector(selectAuth);

  return useCallback(() => {
    if (canGoBackInApp()) {
      navigate(-1);
      return;
    }
    const isDependentsReport =
      userInfo?.role?.name === UserRoleName.Patient &&
      !!reportPatientId &&
      Number(reportPatientId) !== Number(userInfo.id);
    roleNavigate(isDependentsReport ? `/my-care?patientId=${reportPatientId}#care_history` : '/my-care#care_history');
  }, [navigate, reportPatientId, roleNavigate, userInfo]);
}
